import React from 'react';
import {useApolloClient, useMutation} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {Button} from '@mui/material';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  GENERATE_RECOMMENDATION_POT,
  UPSERT_RECOMMENDATION,
} from '../RecommendsGrid/ovPotQueries';
import updateRec from './getRecommendationVariables';
import {FINALISE_RECOMMEND} from '../CreateRecommend/createRecommendQueries';
import {Pot} from '../../../schema-types/Pot';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';

import {getAdvicePlan, getAdviceWhat, getIsMinor, updateRecsVarsProps} from './helpers';
import {
  FinaliseOvrecommend,
  FinaliseOvrecommendVariables,
} from '../../../schema-types/FinaliseOvrecommend';
import {
  UpsertOvrecommend,
  UpsertOvrecommendVariables,
} from '../../../schema-types/UpsertOvrecommend';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {
  MAIN_PLAN_NEW,
  MAIN_PLAN_TYPE,
  MAIN_PLAN_TYPE_MINOR,
  useRecommendContext,
} from '../CreateRecommend/CreateRecommendHelpers';

import {
  GenerateOvrecommendPots,
  GenerateOvrecommendPotsVariables,
} from '../../../schema-types/GenerateOvrecommendPots';
import validateUUID from '../../../utils/validateUUID';

export interface createUpdateProps {
  values: recommendFieldsType;
  contactName: string;
  sourcePots: Pot[];
  destinationPots: Pot[];
  shouldFinalise: boolean;
}

export default function useSaveRecommendation(
  setFinalising: (val: boolean) => void,
  setSavingDraft: (val: boolean) => void,
  type: recommendFieldsType['how']
) {
  const {reviewId} = useReviewContext();

  function closeModal() {
    setFinalising(false);
    setSavingDraft(false);
  }

  const {pots, account, accountId} = useAccountContext();
  const {cache} = useApolloClient();
  const {recommendDetails, ovRecommendId, setOvRecommendId} = useRecommendContext();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const onSuccess = (id: string) => {
    navigate({pathname: `../requirements`, search: `previous=${id}`});
  };
  const onError = () => {
    enqueueSnackbar(`Error saving ${type}`, {variant: 'error'});
    closeModal();
  };

  const [generateRecommendationPots] = useMutation<
    GenerateOvrecommendPots,
    GenerateOvrecommendPotsVariables
  >(GENERATE_RECOMMENDATION_POT);

  const [upsertRecommendation] = useMutation<
    UpsertOvrecommend,
    UpsertOvrecommendVariables
  >(UPSERT_RECOMMENDATION, {
    onError,
    refetchQueries: ['ReviewDetails', 'GetSppReview'],
  });
  const [finaliseMutation] = useMutation<
    FinaliseOvrecommend,
    FinaliseOvrecommendVariables
  >(FINALISE_RECOMMEND, {
    refetchQueries: ['ReviewDetails', 'GetSppReview'],
  });
  const createUpdate: (props: createUpdateProps) => Promise<any> = async ({
    values,
    contactName,
    sourcePots,
    destinationPots,
    shouldFinalise = false,
  }) => {
    // If this is a sub-recommendation, use the parent's goal
    const goalId = values.goal?.id;
    // Get the goal ID from the parent recommendation
    // if (values.parentId) {
    //   const parent: OVRecommendation = cache.readFragment({
    //     // eslint-disable-next-line no-underscore-dangle
    //     id: `OVRecommendType:${values.parentId}`,
    //     fragmentName: 'OVRecommend',
    //     fragment: OV_RECOMMENDATION,
    //   }) as OVRecommendation;

    //   if (!parent?.goal?.id) {
    //     throw new Error(`Unable to find the parent ${values.how}`);
    //   }

    //   goalId = parent.goal.id;
    // }

    if (!values.mainPlanType && values.planAction !== OVRecommendActionEnum.ACTION) {
      enqueueSnackbar('Cannot save without selecting plan type', {
        variant: 'error',
        autoHideDuration: null,
      });
      return {};
    }

    if (!goalId && values?.how !== 'Instruction') {
      enqueueSnackbar('Cannot save without selecting a goal', {
        variant: 'error',
        autoHideDuration: null,
      });
      return {};
    }

    if (getIsMinor(values.contact, account?.contacts || [])) {
      const isNewPlan =
        values.setUpNew?.newPlan ||
        values.transfer?.destinationAccount === 'newPlan' ||
        values.contribute?.contributeTo === 'newPlan';

      if (isNewPlan) {
        const parentGuardianId =
          values.setUpNew?.newPlan?.parentGuardianId ||
          values.transfer?.newPlan?.parentGuardianId ||
          values.contribute?.newPlan?.parentGuardianId;
        if (!parentGuardianId) {
          enqueueSnackbar('Signing parent required', {
            variant: 'error',
            autoHideDuration: null,
          });
          return {};
        }
      }
    }

    const loadingSnackbar = enqueueSnackbar('Saving...', {
      variant: 'info',
      persist: true,
      action: (snackbarId) => (
        <>
          <Button
            type="button"
            onClick={() => {
              closeSnackbar(snackbarId);
              setFinalising(false);
              setSavingDraft(false);
            }}
          >
            Cancel
          </Button>
        </>
      ),
    });

    const updateRecsVars = {
      accountId: account?.id || accountId,
      goalId,
      pots,
      values,
      generateRecommendationPots,
      contactName,
      upsertRecommendation,
      recommendDetails,
      sourcePots,
      destinationPots,
      enqueueSnackbar,
      reviewId,
      contacts: account?.contacts || [],
    };

    type SaltusAny = any;
    try {
      const existingOvrecommendId = recommendDetails?.id;
      // update the requirement in all cases
      const vars: updateRecsVarsProps = {
        ovrecommendId: existingOvrecommendId,
        draft: !shouldFinalise,
        ...updateRecsVars,
      };

      // Compute derived advice fields
      const allPlanTypes = [...MAIN_PLAN_TYPE, ...MAIN_PLAN_TYPE_MINOR, ...MAIN_PLAN_NEW];
      const adviceWhat = getAdviceWhat(values.planAction);
      const advicePlan = getAdvicePlan(values.mainPlanType, allPlanTypes);

      const ovrecommendId = await updateRec(vars, adviceWhat, advicePlan);

      if (shouldFinalise) {
        const finalisation = await finaliseMutation({
          variables: {
            ovrecommendId: validateUUID(ovrecommendId)!,
          },
        });
        const finaliseOvrecommend = finalisation.data?.finaliseOvrecommend;

        if (!finaliseOvrecommend?.ok || finalisation.errors?.length) {
          // Combine FinaliseOVRecommend's `errors` list (BE business
          // errors, e.g. data-validation gate failures from ON-8998)
          // with any GraphQL/network errors so the snackbar surfaces
          // the actual reason. The previous code only joined network
          // errors and produced an empty message when ok=false was
          // driven by a business rule.
          const messages = [
            ...(finaliseOvrecommend?.errors ?? []),
            ...(finalisation.errors?.map((e) => e.message) ?? []),
          ];
          throw Error(`Unable to add ${type}. Error: ${messages.join('; ')}`);
        }
      }
      onSuccess(ovrecommendId);
      enqueueSnackbar(`${type} ${values.id ? 'updated' : 'added'}`, {
        variant: 'success',
      });
    } catch (err: SaltusAny) {
      const existingOvrecommendId = recommendDetails?.id;
      if (existingOvrecommendId) {
        setOvRecommendId(existingOvrecommendId);
        window.history.replaceState(
          null,
          document.title,
          `${window.location.pathname}?ovrecommendId=${existingOvrecommendId}`
        );
      }
      console.error(err);
      if (err instanceof Error) {
        const errorFormatted = (err as Error).message
          .replace(
            /Contact with id: (.+) doesn't exist/,
            'Contact is not set up with the correct business unit'
          )
          .replace(/pot/i, 'plan');
        enqueueSnackbar(`Error saving this ${values.how}. Error: ${errorFormatted}`, {
          variant: 'error',
          autoHideDuration: null,
        });
      }
      closeModal();
    } finally {
      closeSnackbar(loadingSnackbar);
    }
  };
  return {
    createUpdate,
  };
}
